import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';
import {
  createEmailTemplate,
  deleteEmailTemplate,
  fetchEmailTemplates,
  isUniqueViolation,
  updateEmailTemplate,
} from '@/services/email-templates.service';
import { EmailTemplatePayload } from '@/lib/admin/email-templates/models/email-template.model';

export const EMAIL_TEMPLATES_QUERY_KEY = 'email-templates';

/** Lista completa dei template (nessuna paginazione: la tabella è piccola). */
export const useEmailTemplatesQuery = () =>
  useQuery({
    queryKey: [EMAIL_TEMPLATES_QUERY_KEY],
    queryFn: fetchEmailTemplates,
  });

/**
 * Insert di un nuovo template. La collisione sulla PK (`23505`) arriva anche a
 * fronte del controllo di unicità già fatto sulla cache: due admin possono
 * scrivere in parallelo, quindi il messaggio dedicato resta necessario.
 */
export const useCreateEmailTemplate = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (payload: EmailTemplatePayload) => createEmailTemplate(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EMAIL_TEMPLATES_QUERY_KEY] });
      toast({
        variant: 'success',
        title: t('emailTemplates.toast.createdTitle'),
        description: t('emailTemplates.toast.createdDescription'),
      });
    },
    onError: (error: unknown) => {
      console.error('Error creating email template:', error);
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: isUniqueViolation(error)
          ? t('emailTemplates.errors.duplicateKey')
          : t('emailTemplates.toast.saveErrorDescription'),
      });
    },
  });
};

export const useUpdateEmailTemplate = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useI18n();

  return useMutation({
    mutationFn: ({ key, payload }: { key: string; payload: EmailTemplatePayload }) =>
      updateEmailTemplate(key, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EMAIL_TEMPLATES_QUERY_KEY] });
      toast({
        variant: 'success',
        title: t('emailTemplates.toast.updatedTitle'),
        description: t('emailTemplates.toast.updatedDescription'),
      });
    },
    onError: (error: unknown) => {
      console.error('Error updating email template:', error);
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: t('emailTemplates.toast.saveErrorDescription'),
      });
    },
  });
};

export const useDeleteEmailTemplate = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (key: string) => deleteEmailTemplate(key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EMAIL_TEMPLATES_QUERY_KEY] });
      toast({
        variant: 'success',
        title: t('emailTemplates.toast.deletedTitle'),
        description: t('emailTemplates.toast.deletedDescription'),
      });
    },
    onError: (error: unknown) => {
      console.error('Error deleting email template:', error);
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: t('emailTemplates.toast.deleteErrorDescription'),
      });
    },
  });
};
